import React, { useState } from 'react'
import Modal from '@material-ui/core/Modal'
import orderBy from 'lodash/orderBy'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import { useQuery } from '@apollo/client'
import Backdrop from '@material-ui/core/Backdrop'
import Fade from '@material-ui/core/Fade'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import TablePagination from '@material-ui/core/TablePagination'
import CircularProgress from '@material-ui/core/CircularProgress'
import { GET_EVENT_DETAILS } from '../../../network/events/queries'
import { useStyles } from './styles'

const headCells = [
  { id: 'name', disablePadding: true, label: 'Name' },
  { id: 'email', disablePadding: true, label: 'Email' },
  { id: 'invitation', disablePadding: false, label: 'Invitation' },
  { id: 'rsvp', disablePadding: false, label: 'RSVP' },
  { id: 'audience', disablePadding: false, label: 'Audience' },
  { id: 'lifeLab', disablePadding: false, label: 'Life Lab' },
  { id: 'level', disablePadding: false, label: 'Level', align: 'center' },
  { id: 'step', disablePadding: false, label: 'Step', align: 'center' },
  { id: 'signups', disablePadding: false, label: 'Sign-up', align: 'center' },
  { id: 'attending', disablePadding: false, label: 'Attending', align: 'center' }
  // { id: 'signups', disablePadding: false, label: 'Signups' },
]

const EnhancedTableHead = ({ classes, order, orderByField, onRequestSort }) => {
  const createSortHandler = property => event => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow className={classes.headerRow}>
        {headCells.map(headCell => (
          <TableCell
            style={{ minWidth: headCell.minWidth || undefined }}
            key={headCell.id}
            align={headCell.align || 'left'}
            padding='none'
            sortDirection={orderByField === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderByField === headCell.id}
              direction={orderByField === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const EventDetailsModal = ({ isModalOpen, handleModalClose, eventId }) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [order, setOrder] = useState('asc')
  const [orderByField, setOrderBy] = useState('name')

  const { loading, data } = useQuery(GET_EVENT_DETAILS, {
    variables: {
      eventId
    },
    fetchPolicy: 'no-cache'
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderByField === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const closeModal = () => {
    handleModalClose()
  }

  const getComparator = signup => {
    const { user, invitation, audience, userActiveLab, myPathCurrentPosition, signed, attended } = signup
    switch (orderByField) {
      case 'name':
        const fullName = `${user.firstName} ${user.lastName}`
        return fullName.toLowerCase()
      case 'email':
        return user.email.toLowerCase()
      case 'invitation':
        return invitation.name.toLowerCase()
      case 'rsvp':
        return Number(invitation.rsvp)
      case 'audience':
        return audience.name.toLowerCase()
      case 'lifeLab':
        return userActiveLab?.name.toLowerCase()
      case 'level':
        return myPathCurrentPosition?.currentPosition?.level?.index || ''
      case 'step':
        return myPathCurrentPosition?.currentPosition?.step?.index || ''
      case 'signups':
        return signed
      case 'attending':
        return attended
      default:
        return ''
    }
  }

  const eventSignUps = get(data, 'coordinator.event.eventSignUps', [])
  const userSignUpsCount = get(data, 'coordinator.event.byId.userSignUpsCount', 0)
  const userAcceptedCount = get(data, 'coordinator.event.byId.userAcceptedCount', 0)
  const userAttendedCount = get(data, 'coordinator.event.byId.userAttendedCount', 0)
  const maxSignUps = get(data, 'coordinator.event.byId.maxSignUps', null)
  const eventsState = get(data, 'coordinator.event.byId.state', '')
  const tableData = orderBy(eventSignUps, getComparator, order)

  return (
    <Modal
      className={classes.modal}
      open={isModalOpen}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Fade in={isModalOpen}>
        <div className={classes.modalWrapper}>
          {loading || !Object.keys(data).length ? (
            <div className={classes.loader}>
              <CircularProgress color='inherit' />
            </div>
          ) : (
            <div className={classes.modalBody}>
              <div className={classes.modalHeader}>
                <Button onClick={closeModal} variant='outlined' className={classes.closeModalButton}>
                  CLOSE
                </Button>
              </div>
              <div className={classes.modalContent}>
                <div className={classes.header}>
                  <Typography variant='body1'>{`Invited: ${userSignUpsCount}`}</Typography>
                  <Typography variant='body1'>{`Attended: ${eventsState === 'ended' ? userAttendedCount : '-'}`}</Typography>
                  <Typography variant='body1'>{`Max Sign-up: ${maxSignUps || '-'}`}</Typography>
                  <Typography variant='body1'>{`RSVP/Follow: ${userAcceptedCount}`}</Typography>
                </div>
                <div className={classes.modalTableContainer}>
                  <TableContainer>
                    <Table className={classes.table} size='small'>
                      <EnhancedTableHead classes={classes} order={order} orderByField={orderByField} onRequestSort={handleRequestSort} />
                      <TableBody>
                        {tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(signup => {
                          return (
                            <TableRow className={classes.tableRow} hover tabIndex={-1} key={signup.id}>
                              <TableCell>{`${signup.user.firstName} ${signup.user.lastName}`}</TableCell>
                              <TableCell align='left'>{signup.user.email}</TableCell>
                              <TableCell align='left'>{signup.invitation.name}</TableCell>
                              <TableCell align='left'>{signup.invitation.rsvp.toString()}</TableCell>
                              <TableCell align='left'>{signup.audience.name}</TableCell>
                              <TableCell align='left'>{signup.userActiveLab?.name}</TableCell>
                              <TableCell align='center'>{signup.myPathCurrentPosition?.currentPosition?.level?.index}</TableCell>
                              <TableCell align='center'>{signup.myPathCurrentPosition?.currentPosition?.step?.index}</TableCell>
                              <TableCell align='center'>{isNil(signup.signed) ? '-' : signup.signed ? 'Yes' : 'No'}</TableCell>
                              <TableCell align='center'>
                                {eventsState !== 'ended' ? '-' : signup.attended ? 'Attended' : 'Not attended'}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    className={classes.pagination}
                    rowsPerPageOptions={[5, 10, 25]}
                    component='div'
                    count={eventSignUps.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Fade>
    </Modal>
  )
}

export default EventDetailsModal
